import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Coins, MapPin, TrendingUp, ExternalLink } from 'lucide-react';
import ChainBadge from '../ChainBadge';
import { getEnabledEVMChains, isEVMAddress } from '../chainUtils';

/**
 * Helper function to shorten hex address
 * Format: 0x1234...5678 (first 6 + last 4 characters)
 */
const shortenAddress = (address) => {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

/**
 * AddressResultItem - Shows a single address result for a specific chain
 */
const AddressResultItem = ({ address, chain, onClose }) => {
  const displayAddress = /^0x[a-fA-F0-9]{40}$/.test(address.displayText)
    ? shortenAddress(address.displayText)
    : address.displayText;

  return (
    <Link
      to={`/chain/${chain}/${address.address}`}
      onClick={onClose}
      className="w-full px-4 py-3 hover:bg-blue-50 text-left transition-colors flex items-center justify-between group"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-sm text-gray-900">
            {displayAddress}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <ChainBadge chain={chain} size="sm" />
          <span className="text-sm text-gray-600 capitalize min-w-[80px] text-right">
            {chain}
          </span>
        </div>
      </div>
      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0 ml-3" />
    </Link>
  );
};

/**
 * SearchResultsDropdown - Shows categorized search results
 *
 * @param {Object} props
 * @param {Array} props.addresses - Array of address results
 * @param {Array} props.coins - Array of coin results
 * @param {Function} props.onClose - Callback to close dropdown
 * @param {boolean} props.show - Whether to show dropdown
 */
const SearchResultsDropdown = ({
  addresses = [],
  coins = [],
  onClose,
  show
}) => {
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show, onClose]);

  if (!show) return null;

  // Expand EVM addresses into multiple chain entries
  const expandedAddresses = [];
  addresses.forEach(address => {
    if (isEVMAddress(address.displayText)) {
      // For EVM addresses, create an entry for each enabled EVM chain
      const evmChains = getEnabledEVMChains();
      evmChains.forEach(chainInfo => {
        expandedAddresses.push({
          ...address,
          chain: chainInfo.id,
          isEVM: true
        });
      });
    } else {
      // For non-EVM addresses, keep as-is
      expandedAddresses.push({
        ...address,
        isEVM: false
      });
    }
  });

  const hasResults = expandedAddresses.length > 0 || coins.length > 0;

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto z-50"
    >
      {!hasResults ? (
        <div className="p-6 text-center text-gray-500">
          <div className="text-4xl mb-2">🔍</div>
          <p className="font-medium">No results found</p>
          <p className="text-sm mt-1">Try searching for:</p>
          <ul className="text-sm mt-2 space-y-1">
            <li>• A coin name (e.g., "Bitcoin", "Ethereum")</li>
            <li>• A coin symbol (e.g., "BTC", "UNI")</li>
            <li>• A blockchain address (e.g., "0x742d35...")</li>
            <li>• A token contract address</li>
          </ul>
        </div>
      ) : (
        <>
          {/* Coins Section */}
          {coins.length > 0 && (
            <div className="border-b border-gray-100">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                <Coins className="w-3 h-3" />
                Coins & Tokens
              </div>
              <div className="divide-y divide-gray-100">
                {coins.map((coin) => (
                  <Link
                    key={coin.id}
                    to={`/coin/${coin.id}`}
                    onClick={onClose}
                    className="w-full px-4 py-3 hover:bg-blue-50 text-left transition-colors flex items-center gap-3 group"
                  >
                    {/* Coin Logo */}
                    <div className="flex-shrink-0">
                      <img
                        src={coin.image}
                        alt={coin.name}
                        className="w-8 h-8 rounded-full"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32"%3E%3Crect width="32" height="32" fill="%23ddd"/%3E%3C/svg%3E';
                        }}
                      />
                    </div>

                    {/* Coin Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 truncate">
                          {coin.name}
                        </span>
                        <span className="text-xs text-gray-500 uppercase font-mono">
                          {coin.symbol}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600">
                        {coin.market_cap_rank && (
                          <span className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            Rank #{coin.market_cap_rank}
                          </span>
                        )}
                        {coin.current_price && (
                          <span className="font-semibold">
                            ${coin.current_price.toLocaleString()}
                          </span>
                        )}
                        {coin.price_change_percentage_24h !== null && coin.price_change_percentage_24h !== undefined && (
                          <span className={coin.price_change_percentage_24h >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {coin.price_change_percentage_24h >= 0 ? '+' : ''}
                            {coin.price_change_percentage_24h.toFixed(2)}%
                          </span>
                        )}
                      </div>
                    </div>

                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Addresses Section */}
          {expandedAddresses.length > 0 && (
            <div className="border-b border-gray-100">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                <MapPin className="w-3 h-3" />
                Addresses
              </div>
              <div className="divide-y divide-gray-100">
                {expandedAddresses.map((address, index) => (
                  <AddressResultItem
                    key={`address-${address.chain}-${index}`}
                    address={address}
                    chain={address.chain}
                    onClose={onClose}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Footer with hint */}
      {hasResults && (
        <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-center border-t border-gray-100">
          Click on a result to view details
        </div>
      )}
    </div>
  );
};

export default SearchResultsDropdown;
